import { supabase } from '@/lib/supabase';
import type { FlowExecutionStatus } from '@shared/types/flow-executions';
import {
  extractInternalDiagnosisFromError,
  extractProductDiagnosisFromError,
  type InternalDiagnosis,
  type ProductDiagnosis,
} from '@shared/types/product-diagnosis';

/** Per-run result visibility (RLS-enforced): mine = owner only (default). */
export type RunResultScope = 'mine' | 'team' | 'tenant';

export const updateRunResultScope = async (params: {
  executionId: string;
  scope: RunResultScope;
}): Promise<void> => {
  const { error } = await supabase
    .from('flow_executions')
    .update({ result_scope: params.scope })
    .eq('id', params.executionId);

  if (error) {
    throw new Error(error.message || 'Impossible de modifier le partage de ce résultat.');
  }
};

export interface ToolRunListItem {
  id: string;
  userId: string;
  title: string;
  summary: string | null;
  status: FlowExecutionStatus;
  startedAt: string | null;
  updatedAt: string;
  completedAt: string | null;
  displayName: string | null;
  productDiagnosis: ProductDiagnosis | null;
  /** Technical, planner-facing diagnosis used to drive repair. Never rendered. */
  internalDiagnosis: InternalDiagnosis | null;
  subjectId: string | null;
  accountSubjectId: string | null;
}

interface FlowExecutionRow {
  id: string;
  user_id: string;
  status: FlowExecutionStatus;
  started_at: string | null;
  updated_at: string;
  completed_at: string | null;
  execution_summary: unknown;
  view_payload: unknown;
  error: unknown;
  subject_id: string | null;
  account_subject_id: string | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const getSummaryTitle = (value: unknown): string | null => {
  if (!isRecord(value) || typeof value.title !== 'string') {
    return null;
  }

  const title = value.title.trim();
  return title.length > 0 ? title : null;
};

const getSummaryNarration = (value: unknown): string | null => {
  if (!isRecord(value) || typeof value.narration !== 'string') {
    return null;
  }

  const narration = value.narration.trim();
  return narration.length > 0 ? narration : null;
};

const summarizeArtifactBody = (value: unknown): string | null => {
  if (!isRecord(value) || value.view_type !== 'artifact_document') return null;
  const data = isRecord(value.data) ? value.data : null;
  if (!data || typeof data.body !== 'string') return null;

  const lines = data.body
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  const summaryHeadingIndex = lines.findIndex((line) =>
    /^#{1,6}\s*(synth[eè]se|r[eé]sum[eé])/i.test(line)
  );
  const candidates = summaryHeadingIndex >= 0
    ? lines.slice(summaryHeadingIndex + 1)
    : lines;
  const summary = candidates.find(
    (line) => !line.startsWith('#') && !/^[-*]\s/.test(line) && line.length > 20
  );

  if (!summary) return null;
  const plain = summary
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .trim();
  return plain.length > 220 ? `${plain.slice(0, 219).trim()}…` : plain;
};

export const getToolRunDisplaySummary = (
  executionSummary: unknown,
  viewPayload: unknown
): string | null => {
  const narration = getSummaryNarration(executionSummary);
  if (narration && !/^[{[]/.test(narration)) return narration;
  return summarizeArtifactBody(viewPayload);
};

const getFallbackTitle = (status: FlowExecutionStatus, index: number): string => {
  switch (status) {
    case 'failed':
      return 'Tâche échouée';
    case 'cancelled':
      return 'Tâche annulée';
    case 'completed':
      return `Tâche ${index + 1}`;
    case 'pending':
      return 'Tâche en file';
    case 'paused':
      return 'Tâche en attente';
    case 'running':
    default:
      return 'Tâche en cours';
  }
};

const loadDisplayNamesByUserId = async (userIds: string[]): Promise<Record<string, string>> => {
  if (userIds.length === 0) return {};

  const { data, error } = await supabase
    .from('tenant_members')
    .select('user_id, display_name')
    .in('user_id', userIds);

  if (error) {
    throw new Error(error.message || 'Impossible de charger les noms des membres.');
  }

  return (Array.isArray(data) ? data : []).reduce<Record<string, string>>((accumulator, entry) => {
    if (typeof entry.user_id === 'string' && typeof entry.display_name === 'string' && entry.display_name.trim().length > 0) {
      accumulator[entry.user_id] = entry.display_name.trim();
    }
    return accumulator;
  }, {});
};

export const listToolRuns = async (params: {
  flowId: string;
  currentUserId: string;
  scope: 'mine' | 'team';
  limit?: number;
  runId?: string;
}): Promise<ToolRunListItem[]> => {
  const limit = params.limit ?? 12;
  let query = supabase
    .from('flow_executions')
    .select('id, user_id, status, started_at, updated_at, completed_at, execution_summary, view_payload, error, subject_id, account_subject_id')
    .eq('flow_id', params.flowId)
    .in('status', ['pending', 'running', 'paused', 'completed', 'failed', 'cancelled'])
    .order('updated_at', { ascending: false, nullsFirst: false })
    .limit(limit);

  if (params.scope === 'mine') {
    query = query.eq('user_id', params.currentUserId);
  } else {
    query = query.neq('user_id', params.currentUserId);
  }
  if (params.runId) query = query.eq('id', params.runId);

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message || 'Impossible de charger les tâches de ce tool.');
  }

  const rows = (Array.isArray(data) ? data : []) as FlowExecutionRow[];
  const displayNamesByUserId = params.scope === 'team'
    ? await loadDisplayNamesByUserId([...new Set(rows.map((entry) => entry.user_id))])
    : {};

  return rows
    .map((row, index) => {
      const productDiagnosis = extractProductDiagnosisFromError(row.error);
      const internalDiagnosis = extractInternalDiagnosisFromError(row.error);
      return {
        id: row.id,
        userId: row.user_id,
        title: productDiagnosis?.title
          ?? getSummaryTitle(row.execution_summary)
          ?? getFallbackTitle(row.status, index),
        summary: productDiagnosis?.explanation
          ?? getToolRunDisplaySummary(row.execution_summary, row.view_payload),
        status: row.status,
        startedAt: row.started_at,
        updatedAt: row.updated_at,
        completedAt: row.completed_at,
        displayName: row.user_id === params.currentUserId
          ? 'You'
          : (displayNamesByUserId[row.user_id] ?? null),
        productDiagnosis,
        internalDiagnosis,
        subjectId: row.subject_id,
        accountSubjectId: row.account_subject_id,
      };
    })
    .sort((left, right) => {
      const leftIsActive = left.status !== 'completed';
      const rightIsActive = right.status !== 'completed';
      if (leftIsActive !== rightIsActive) return leftIsActive ? -1 : 1;

      const leftTimestamp = leftIsActive
        ? (left.updatedAt || left.startedAt || left.completedAt || '')
        : (left.completedAt || left.updatedAt || '');
      const rightTimestamp = rightIsActive
        ? (right.updatedAt || right.startedAt || right.completedAt || '')
        : (right.completedAt || right.updatedAt || '');

      return rightTimestamp.localeCompare(leftTimestamp);
    });
};
